<template>
  <div class="filepond-uploader">
    <h6 class="mb-2">Upload Photos</h6>
    <p class="text-muted small mb-3">Drag & drop images or click to browse</p>

    <FilePondComponent
      ref="pond"
      name="filepond"
      :allow-multiple="true"
      :files="pondFiles"
      :accepted-file-types="acceptedFileTypes"
      :label-idle="labelIdle"
      :server="server"
      @processfiles="handleAllUploads"
    />
  </div>
</template>

<script setup>
/* eslint-disable */
import { ref } from 'vue'
import Swal from 'sweetalert2'
import api from '@/services/api'

// ✅ Props & Emits
const props = defineProps({
  eventid: { type: [String, Number], required: true },
  userid: { type: [String, Number], required: true }
})
const emit = defineEmits(['upload-complete'])

// ✅ Import FilePond and plugins
import * as FilePond from 'vue-filepond'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css'

// ✅ Create FilePond component
const FilePondComponent = FilePond.default(FilePondPluginImagePreview, FilePondPluginFileValidateType)

// ✅ Refs
const pond = ref(null)
const pondFiles = ref([])

const acceptedFileTypes = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/svg+xml'
]

const labelIdle =
  '📸 Drop your images here, or <span class="filepond--label-action">Browse</span> to add photos.'

// ✅ Toast utility
const toast = (msg, icon = 'success') =>
  Swal.fire({
    toast: true,
    position: 'top-end',
    icon,
    title: msg,
    timer: 3000,
    showConfirmButton: false
  })

// ✅ Server configuration
const server = {
  process: (fieldName, file, metadata, load, error, progress, abort) => {
    const formData = new FormData()
    formData.append('img', file)
    formData.append('user_id', props.userid)
    formData.append('event_id', props.eventid)

    // Use your API service
    const request = api
      .AlbumUploadImages(formData, {
        onUploadProgress: (e) => progress(true, e.loaded, e.total)
      })
      .then((res) => {
        if (res.data.status === 'ok') {
          load(res.data.filename || file.name)
          toast('Image uploaded successfully')
        } else {
          error('Upload failed')
          toast('Upload failed', 'error')
        }
      })
      .catch((err) => {
        console.error('Upload failed:', err)
        error('Upload failed')
        toast('Upload failed', 'error')
      })

    return {
      abort: () => {
        console.log('Upload aborted')
        abort()
      }
    }
  }
}

// ✅ When all uploads are done
function handleAllUploads() {
  toast('✅ All images uploaded successfully!')
  pond.value.removeFiles()
  emit('upload-complete')
}
</script>

<style scoped>
.filepond-uploader {
  text-align: center;
  margin-bottom: 1rem;
}

/* 🔹 FilePond Custom Styles */
.filepond--list {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 16px !important;
  justify-content: flex-start;
  box-sizing: border-box;
}

.filepond--item {
  width: calc(30% - 12px);
  margin: 10px !important;
  padding: 5px !important;
  box-sizing: border-box;
}

.filepond--file {
  padding: 0 !important;
  margin: 0 !important;
}

.filepond--panel-root {
  border-radius: 10px;
  overflow: hidden;
}

.filepond--drop-label {
  padding: 1rem 0.5rem;
  font-size: 1rem;
  font-weight: 500;
}

/* 🔹 Responsive adjustments */
@media screen and (max-width: 992px) {
  .filepond--item {
    width: calc(30% - 10px);
  }
}

@media screen and (max-width: 768px) {
  .filepond--item {
    width: calc(50% - 12px);
  }
}

@media screen and (max-width: 480px) {
  .filepond--item {
    width: 100%;
  }
}
</style>
